/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, OnInit } from '@angular/core';
import { COMMON_IMPORTS } from '../../common-imports';
import { PremyopeAgeCategory, PremyopeRiskScoreCategory } from '../../backend-response-models/calc-enums.helper';
import { TranslatePipe } from '../../languages/translate.pipe';
import Mustache from 'mustache';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { ChartModule } from 'primeng/chart';
import { blackColorTL, greenColorTL, orangeColorTL, redColorTL, whiteColorTL, yellowColorTL } from '../../helpers/ui.helper';

@Component({
  selector: 'app-premyopia-risk-score',
  standalone: true,
  imports: [...COMMON_IMPORTS, CardModule, PanelModule, ChartModule],
  templateUrl: './premyopia-risk-score.component.html',
  styleUrl: './premyopia-risk-score.component.css',
})
export class PremyopiaRiskScoreComponent implements OnInit {
  PremyopeAgeCategory = PremyopeAgeCategory;
  @Input()
  lang?: string;

  @Input({ required: true })
  premyopeTotalRiskScore!: number;

  @Input({ required: true })
  serRiskScore!: number;

  @Input({ required: true })
  axialRiskScore!: number;

  @Input({ required: true })
  inheritabilityRiskScore!: number;

  @Input({ required: true })
  ageCatagory!: PremyopeAgeCategory;

  @Input({ required: true })
  premyopeTotalRiskScoreCatagory!: PremyopeRiskScoreCategory;

  @Input()
  personalization?: string;

  public catagoryTranslationKey: string = 'NONE';
  public dialSweepAngle: number = 180;

  readonly allBreakdownColors: string[] = [greenColorTL, yellowColorTL, yellowColorTL, redColorTL];
  readonly allBreakdownTextColors: string[] = [whiteColorTL, blackColorTL, blackColorTL, whiteColorTL];

  public readonly catagoryTranslationKeys: Record<PremyopeRiskScoreCategory, string> = {
    [PremyopeRiskScoreCategory.LITTLE_TO_NO_RISK]: 'PREMYOPIA_CATAGORY_LITTLE_TO_NO_RISK',
    [PremyopeRiskScoreCategory.LITTLE_RISK]: 'PREMYOPIA_CATAGORY_LITTLE_RISK',
    [PremyopeRiskScoreCategory.MODERATE_RISK]: 'PREMYOPIA_CATAGORY_MODERATE_RISK',
    [PremyopeRiskScoreCategory.HIGH_RISK]: 'PREMYOPIA_CATAGORY_HIGH_RISK',
  };

  private readonly predictionTemplateKeys: Record<PremyopeRiskScoreCategory, string> = {
    [PremyopeRiskScoreCategory.LITTLE_TO_NO_RISK]: 'LITTLE_NO_MYOPIA_RISK_SCORE_PREDICTION_TEMPLATE',
    [PremyopeRiskScoreCategory.LITTLE_RISK]: 'LITTLE_MYOPIA_RISK_SCORE_PREDICTION_TEMPLATE',
    [PremyopeRiskScoreCategory.MODERATE_RISK]: 'MODERATE_MYOPIA_RISK_SCORE_PREDICTION_TEMPLATE',
    [PremyopeRiskScoreCategory.HIGH_RISK]: 'HIGH_MYOPIA_RISK_SCORE_PREDICTION_TEMPLATE',
  };

  private readonly segmentColors: string[] = [
    greenColorTL,
    yellowColorTL,
    yellowColorTL,
    yellowColorTL,
    orangeColorTL,
    orangeColorTL,
    orangeColorTL,
    redColorTL,
    redColorTL,
    redColorTL,
  ];

  private readonly textColors: string[] = [
    whiteColorTL,
    blackColorTL,
    blackColorTL,
    blackColorTL,
    whiteColorTL,
    whiteColorTL,
    whiteColorTL,
    whiteColorTL,
    whiteColorTL,
    whiteColorTL,
  ];

  public predictionText: string = 'NONE';
  public data: any;
  public options: any;
  public indicatorStyle: any;
  public riskColor: string = 'FFFFFF';
  public riskColorText: string = 'FFFFFF';

  constructor(private translatePipe: TranslatePipe) {}

  ngOnInit() {
    this.setupComponent();
  }

  getBreakdownColors(score: number) {
    const color = this.allBreakdownColors[score];
    return color;
  }

  getBreakdownTextColors(score: number) {
    const color = this.allBreakdownTextColors[score];
    return color;
  }

  setupComponent() {
    this.setupDataAndGraphs(this.premyopeTotalRiskScore);
    this.updateIndicator(this.premyopeTotalRiskScore);

    this.lang = this.lang ?? 'en';
    this.catagoryTranslationKey = this.catagoryTranslationKeys[this.premyopeTotalRiskScoreCatagory];

    const moustacheTemplate = this.translatePipe.transform(this.predictionTemplateKeys[this.premyopeTotalRiskScoreCatagory]);
    const noNameName = this.translatePipe.transform('NO_NAME_NAME');
    const renderedTemplate = Mustache.render(moustacheTemplate, { PERSONALIZED_NAME: this.personalization ?? noNameName });

    this.predictionText = renderedTemplate;
    this.riskColor = this.segmentColors[this.premyopeTotalRiskScore];
    this.riskColorText = this.textColors[this.premyopeTotalRiskScore];
  }

  updateIndicator(score: number) {
    const segments = this.ageCatagory === PremyopeAgeCategory.GREATER_OR_SAME_AS_9_YEARS ? 7 : 10;
    const segmentAngle = this.dialSweepAngle / segments;
    const startPos = (this.dialSweepAngle / 2) * -1 + segmentAngle / 2;
    const indicatorAngle = startPos + score * segmentAngle;

    const theta = -55 * Math.sin((indicatorAngle * Math.PI) / 180) - 55;
    const beta = 10 * Math.cos((indicatorAngle * Math.PI) / 180);
    console.log('theta', theta);

    this.indicatorStyle = {
      transform: `translate(${theta.toFixed(0)}%, ${beta.toFixed(0)}%) rotate(${indicatorAngle}deg) `,
    };
  }

  setupDataAndGraphs(score: number) {
    const opacity = '20';
    const colors = [...this.segmentColors];
    const colorsWithOpacity = colors.map(color => color + opacity);

    for (let i = 0; i <= score; i++) {
      colorsWithOpacity[i] = colors[i];
    }

    this.data = {
      datasets: [
        {
          data:
            this.ageCatagory === PremyopeAgeCategory.GREATER_OR_SAME_AS_9_YEARS ? [1, 1, 1, 1, 1, 1, 1] : [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
          backgroundColor: colorsWithOpacity,
        },
      ],
    };

    this.options = {
      rotation: (this.dialSweepAngle / 2) * -1, // Start angle (180 degrees)
      circumference: this.dialSweepAngle,
      cutout: '60%',
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        tooltip: {
          enabled: false,
        },
      },
    };
  }
}
